'use client';

import { useEffect, useState, useRef } from 'react';
import { getLocaleCookie, setLocaleCookie } from '@/utils/localeCookiesManager.client';
import { useLocale, useTranslations } from 'next-intl';
import { CWLanguageChangeConfirm } from '@/context/global/components/g00/g00-d00-g020-cw-language-change-confirm';
import { useAuthStore } from '@/context/global/stores/authStore';

const ICON_PATH = '/assets/ic-globe.svg';

export default function CWLocaleToggleButton() {
  const currentLocale = useLocale();
  const t = useTranslations('LocaleToggle');
  const clientType = useAuthStore((state) => state.clientType);

  const [isOpen, setIsOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingLocale, setPendingLocale] = useState<'th' | 'en' | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    const cookieLocale = getLocaleCookie();
    if (cookieLocale !== 'th' && cookieLocale !== 'en') {
      setLocaleCookie('th');
    }
  }, []);

  const handleChange = (newLocale: 'th' | 'en') => {
    if (newLocale === currentLocale) {
      setIsOpen(false);
      return;
    }

    setIsOpen(false);

    // For website users, change language directly without modal
    if (clientType === 'website') {
      setLocaleCookie(newLocale);
      window.location.reload();
      return;
    }

    // For mobile app users, show confirmation modal
    setPendingLocale(newLocale);
    setShowConfirm(true);
  };

  const handleConfirmChange = () => {
    if (pendingLocale) {
      setLocaleCookie(pendingLocale);
      window.location.reload();
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="flex min-w-[48px] cursor-pointer flex-col items-center justify-center gap-1 px-2 py-1 focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <span className="relative flex size-5 shrink-0 items-center justify-center">
          <img src={ICON_PATH} className="block size-full" />
        </span>
        <span className="text-s mt-0.5 font-bold leading-none text-white">
          {currentLocale === 'th' ? 'ไทย' : 'EN'}
        </span>
      </button>

      {isOpen && (
        <div className="absolute -right-20 z-10 mt-1 w-[220px] rounded-[24px] bg-[#232323] shadow-lg">
          <button
            className={`block w-full rounded-t-[24px] px-7 py-4 text-center text-[20px] font-semibold transition-colors hover:cursor-pointer ${currentLocale === 'th' ? 'text-ais-green' : 'text-white hover:text-ais-green'}`}
            onClick={() => handleChange('th')}
          >
            {t('thai')}
          </button>
          <button
            className={`block w-full rounded-b-[24px] border-t border-[#5E5E5E33] px-7 py-4 text-center text-[20px] font-semibold transition-colors hover:cursor-pointer ${currentLocale === 'en' ? 'text-ais-green' : 'text-white hover:text-ais-green'}`}
            onClick={() => handleChange('en')}
          >
            {t('english')}
          </button>
        </div>
      )}

      <CWLanguageChangeConfirm
        isOpen={showConfirm}
        onClose={() => {
          setShowConfirm(false);
          setPendingLocale(null);
        }}
        onConfirm={handleConfirmChange}
      />
    </div>
  );
}
